import * as React from 'react'

import { create } from '@bufbuild/protobuf'
import {
  createConnectQueryKey,
  useMutation,
  useQuery,
} from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'

import {
  I18nProvider,
  LoadingEnechainLogo,
} from '@enechain/ecloud-designsystem'
import {
  getMe,
  updateUserLanguage,
} from '@enechain/eposition-proto/bffportal/v1/user_service-UserService_connectquery'
import { UpdateUserLanguageRequestSchema } from '@enechain/eposition-proto/bffportal/v1/user_service_pb'

import {
  SupportedLanguage,
  isSupportedLanguage,
} from '~/providers/languageStorage'

type LanguageContextType = {
  locale: SupportedLanguage
  setLanguage: (language: SupportedLanguage) => void
}

const LanguageContext = React.createContext<LanguageContextType | undefined>(
  undefined,
)

export const useLanguage = (): LanguageContextType => {
  const context = React.useContext(LanguageContext)
  return (
    context ?? {
      locale: 'ja',
      setLanguage: (): void => undefined,
    }
  )
}

type Props = {
  children: React.ReactNode
}

export const LanguageInitProvider = ({ children }: Props): React.ReactNode => {
  const [languageReady, setLanguageReady] = React.useState(false)
  const [locale, setLocale] = React.useState<SupportedLanguage>('ja')
  // The user's effective language is resolved server-side in GetMe (saved
  // preference, or the eLogin account language as the initial default).
  const { data: meData, isLoading: isMeLoading } = useQuery(
    getMe,
    {},
    { staleTime: Infinity, throwOnError: false },
  )

  const queryClient = useQueryClient()
  const mutation = useMutation(updateUserLanguage, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getMe,
          cardinality: 'finite',
        }),
      })
    },
  })

  // languageReady is set once at startup.
  React.useEffect(() => {
    let cancelled = false

    if (isMeLoading) {
      return undefined
    }

    const serverLanguage = meData?.me?.language
    const resolved: SupportedLanguage = isSupportedLanguage(serverLanguage)
      ? serverLanguage
      : 'ja'
    void i18next
      .changeLanguage(resolved)
      .then(() => {
        if (!cancelled) {
          setLocale(resolved)
          setLanguageReady(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLanguageReady(true)
        }
      })

    return (): void => {
      cancelled = true
    }
  }, [isMeLoading, meData])

  const setLanguage = React.useCallback(
    (language: SupportedLanguage): void => {
      // Switch the UI immediately, then persist the choice server-side.
      void i18next
        .changeLanguage(language)
        .then(() => {
          setLocale(language)
        })
        .catch(() => undefined)
      mutation.mutate(create(UpdateUserLanguageRequestSchema, { language }))
    },
    [mutation],
  )

  const value = React.useMemo<LanguageContextType>(
    () => ({ locale, setLanguage }),
    [locale, setLanguage],
  )

  if (!languageReady) {
    return <LoadingEnechainLogo />
  }

  return (
    <LanguageContext.Provider value={value}>
      <I18nProvider locale={locale}>{children}</I18nProvider>
    </LanguageContext.Provider>
  )
}
